"use client";

import {
    createContext,
    FC,
    forwardRef,
    ForwardRefExoticComponent,
    memo,
    RefAttributes,
    useContext,
    useMemo,
} from "react";
import classNames from "classnames";
import { ICategoryFeedResponse } from "@finbackoffice/websocket-client";
import { FeedRidContext, MultiViewContext, useCollapseMargin } from "@finbackoffice/site-core";
import { useDefaultLiveParamsFromUrl, useDevice, useSocketStorage } from "hooks";
import { getCategoriesEventsBelongTo } from "utils/helpers";
import { FLEX_LAYOUT_WIDTH_BREAKPOINT } from "utils/constants";
import FadeInAnimation from "components/base/fade-in/FadeInAnimation";
import CategoryIcon from "components/sport/base/region-icon/CategoryIcon";
import { LiveTournamentGameLayoutEnum } from "../../tournament/game/LiveTournamentGame";
import LiveTournamentsContainer from "./LiveTournamentsContainer";
import styles from "./live-categories.module.sass";

interface ILiveCategoryProps {
    category: ICategoryFeedResponse;
    sportId: number;
    openedByDefault: boolean;
    showArrow?: boolean;
}

type ILiveCategoriesContext = {
    isTwoColumns: boolean;
    gameLayout?: LiveTournamentGameLayoutEnum;
    variant: LiveCategoriesVariantType;
    feedRid: string;
};

export const LiveCategoriesContext = createContext<ILiveCategoriesContext>(null as any);

const LiveCategory: FC<ILiveCategoryProps> = memo(
    ({ category, sportId, openedByDefault, showArrow = true }) => {
        const { feedRid } = useContext(LiveCategoriesContext);
        const { getTournaments } = useSocketStorage(feedRid);
        const { opened, animating, headerRef, containerRef } = useCollapseMargin(openedByDefault);

        return (
            <FadeInAnimation>
                <div
                    className={classNames(
                        styles.categoryItemsContainer,
                        "live-category",
                        opened && styles.opened,
                    )}>
                    <div className={styles.categoryItem} ref={headerRef}>
                        <CategoryIcon categoryId={category.id} />
                        <span>{category.name}</span>
                        <span className={styles.gameCount}>{category.games_count}</span>
                        {showArrow && <i className={styles.arrow} />}
                    </div>
                    {(opened || animating) && (
                        <LiveTournamentsContainer
                            tournaments={getTournaments(category.id)}
                            sportId={sportId}
                            ref={containerRef}
                            showArrow={showArrow}
                        />
                    )}
                </div>
            </FadeInAnimation>
        );
    },
);

export type LiveCategoriesVariantType = "default" | "sidebar";

interface IProps {
    categories: ICategoryFeedResponse[];
    sportId: number;
    gameLayout?: LiveTournamentGameLayoutEnum;
    showArrow?: boolean;
    variant?: LiveCategoriesVariantType;
    feedRid?: string;
}

const LiveCategories: ForwardRefExoticComponent<IProps & RefAttributes<HTMLDivElement>> =
    forwardRef(
        (
            {
                categories,
                sportId,
                showArrow,
                gameLayout = LiveTournamentGameLayoutEnum.Default,
                feedRid,
                variant = "default",
            },
            ref,
        ) => {
            const { screenWidth } = useDevice();
            const { liveRid } = useContext(FeedRidContext);
            const { isCategorySelected } = useContext(MultiViewContext) ?? {};
            const { categoryId: categoryIdFromUrlParams } = useDefaultLiveParamsFromUrl();
            const defaultOpenedCategoryIds: number[] = useMemo(
                () => getCategoriesEventsBelongTo(categories, 10),
                [categories],
            );

            const categoriesList = useMemo(() => {
                const isSelected = (category: ICategoryFeedResponse) => {
                    if (variant === "sidebar") {
                        return (
                            category.id.toString() === (categoryIdFromUrlParams || "") ||
                            isCategorySelected(category.id)
                        );
                    }
                    return defaultOpenedCategoryIds.indexOf(category.id) !== -1;
                };
                return (
                    <div ref={ref}>
                        {categories.map((category: ICategoryFeedResponse) => (
                            <LiveCategory
                                key={category.id}
                                category={category}
                                sportId={sportId}
                                openedByDefault={isSelected(category)}
                                showArrow={showArrow}
                            />
                        ))}
                    </div>
                );
            }, [
                ref,
                categories,
                variant,
                defaultOpenedCategoryIds,
                categoryIdFromUrlParams,
                isCategorySelected,
                sportId,
                showArrow,
            ]);

            const value = useMemo(
                () => ({
                    isTwoColumns: screenWidth > FLEX_LAYOUT_WIDTH_BREAKPOINT,
                    gameLayout,
                    variant,
                    feedRid: feedRid || liveRid,
                }),
                [screenWidth, gameLayout, variant, feedRid, liveRid],
            );

            return (
                <LiveCategoriesContext.Provider value={value}>
                    <div className={styles.categoryItemsList}>{categoriesList}</div>
                </LiveCategoriesContext.Provider>
            );
        },
    );

export default memo(LiveCategories);
